import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Layers, PackagePlus } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { rupee } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const ROLES = ["manufacturer", "wholesaler", "retailer"];
const EMPTY = { name: "", features: "", price: "", products: "", roles: [...ROLES], active: true, kind: "subscription", beginner: false };

export default function PackagePlanner() {
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("subscription");
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);

  const load = useCallback(async () => { const { data } = await api.get("/admin/packages"); setRows(data); }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditId(null); setForm({ ...EMPTY, kind: tab }); };
  const openEdit = (r) => { setEditId(r.id); setForm({ name: r.name || "", features: r.features || "", price: r.price, products: r.products, roles: [...(r.roles || [])], active: r.active, kind: r.kind || "subscription", beginner: !!r.beginner }); };
  const toggleRole = (role) => setForm((f) => ({ ...f, roles: f.roles.includes(role) ? f.roles.filter((x) => x !== role) : [...f.roles, role] }));

  const save = async () => {
    if (!form.name) { toast.error("Enter a package name"); return; }
    if (!form.price) { toast.error("Enter the amount"); return; }
    if (form.kind !== "crm" && !form.products) { toast.error("Enter number of products"); return; }
    if (!form.roles.length) { toast.error("Tick at least one seller type"); return; }
    const payload = { name: form.name, features: form.features, price: Number(form.price), products: Number(form.products) || 1, roles: form.roles, active: form.active, kind: form.kind, beginner: form.kind === "subscription" && form.beginner };
    try {
      if (editId) await api.put(`/admin/packages/${editId}`, payload);
      else await api.post("/admin/packages", payload);
      toast.success("Package saved");
      setForm(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this package? Sellers will no longer see it.")) return;
    try { await api.delete(`/admin/packages/${id}`); toast.success("Package deleted"); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const isAddon = tab === "addon";
  const isCrm = tab === "crm";
  const visible = rows.filter((r) => (r.kind || "subscription") === tab);
  const subCount = rows.filter((r) => (r.kind || "subscription") === "subscription").length;
  const addonCount = rows.filter((r) => r.kind === "addon").length;
  const crmCount = rows.filter((r) => r.kind === "crm").length;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold flex items-center gap-2"><Layers className="h-6 w-6 text-primary" /> Package Planner</h1>
          <p className="text-muted-foreground text-sm font-medium">Create yearly subscription packages plus optional add-on packages for sellers who've used all their slots. All prices are + GST, valid 1 year.</p>
        </div>
        <Button data-testid="add-package-btn" onClick={openCreate} className="font-bold gap-1"><Plus className="h-4 w-4" /> Add {isAddon ? "Additional" : isCrm ? "Leads CRM" : ""} Package</Button>
      </div>

      <div className="flex gap-2 border-b">
        <button data-testid="tab-subscription" onClick={() => setTab("subscription")}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-bold border-b-2 -mb-px transition-colors ${tab === "subscription" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
          <Layers className="h-4 w-4" /> Subscription Packages <Badge variant="secondary" className="ml-1">{subCount}</Badge>
        </button>
        <button data-testid="tab-addon" onClick={() => setTab("addon")}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-bold border-b-2 -mb-px transition-colors ${tab === "addon" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
          <PackagePlus className="h-4 w-4" /> Additional Packages <Badge variant="secondary" className="ml-1">{addonCount}</Badge>
        </button>
        <button data-testid="tab-crm" onClick={() => setTab("crm")}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-bold border-b-2 -mb-px transition-colors ${tab === "crm" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
          <PackagePlus className="h-4 w-4" /> Leads CRM Plans <Badge variant="secondary" className="ml-1">{crmCount}</Badge>
        </button>
      </div>

      {isCrm && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded-lg px-4 py-2 text-xs font-semibold">
          Leads CRM plans are a paid add-on (e.g. ₹2,500/year + GST). When a seller subscribes, their Leads CRM unlocks — buyer WhatsApp connections, follow-up stages and message templates.
        </div>
      )}

      {isAddon && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-4 py-2 text-xs font-semibold">
          Additional packages are shown to sellers only after they've used up every product slot in their current plan. Buying one adds its products as extra slots.
        </div>
      )}

      <div className="bg-card border rounded-xl overflow-x-auto ct-scroll">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Name</TableHead><TableHead>Amount (₹)</TableHead><TableHead>Products</TableHead><TableHead>Features</TableHead><TableHead>Visible To</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {visible.map((r) => (
              <TableRow key={r.id} data-testid={`package-row-${r.id}`}>
                <TableCell className="font-extrabold uppercase text-xs text-primary">{r.name || "—"}{r.beginner && <Badge className="ml-2 border bg-sky-100 text-sky-800 border-sky-300 normal-case" data-testid={`beginner-badge-${r.id}`}>Beginner · one-time</Badge>}</TableCell>
                <TableCell className="font-bold">{rupee(r.price)} <span className="text-xs text-muted-foreground font-normal">+ GST</span></TableCell>
                <TableCell className="font-semibold">{r.products} products</TableCell>
                <TableCell className="text-xs">{r.features || "—"}</TableCell>
                <TableCell className="capitalize text-xs">{(r.roles || []).join(", ") || "—"}</TableCell>
                <TableCell><Badge className={`border ${r.active ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-slate-100 text-slate-600 border-slate-300"}`}>{r.active ? "Active" : "Hidden"}</Badge></TableCell>
                <TableCell><div className="flex gap-1">
                  <Button size="sm" variant="outline" data-testid={`edit-package-${r.id}`} onClick={() => openEdit(r)}><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="outline" data-testid={`delete-package-${r.id}`} onClick={() => remove(r.id)}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                </div></TableCell>
              </TableRow>
            ))}
            {visible.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground font-semibold">No {isAddon ? "additional" : "subscription"} packages yet. Add one to make it visible to sellers.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!form} onOpenChange={(v) => !v && setForm(null)}>
        <DialogContent className="max-w-md" data-testid="package-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Edit" : "Add"} {form?.kind === "addon" ? "Additional" : form?.kind === "crm" ? "Leads CRM" : "Subscription"} Package</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-4">
              <div><Label>Package Name</Label><Input data-testid="package-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Premium" /></div>
              <div><Label>Features / Description</Label><Input data-testid="package-features" value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} placeholder="e.g. Listing + Instagram promotions" /></div>
              <div><Label>Amount (₹, + GST)</Label><Input data-testid="package-price" type="number" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="e.g. 2500" /></div>
              {form.kind !== "crm" && (
                <div><Label>Number of Products{form.kind === "addon" ? " (extra slots added)" : ""}</Label><Input data-testid="package-products" type="number" min="1" value={form.products} onChange={(e) => setForm({ ...form, products: e.target.value })} placeholder="e.g. 5" /></div>
              )}
              <div>
                <Label>Valid for Seller Types</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {ROLES.map((role) => (
                    <label key={role} className="flex items-center gap-2 border rounded-lg px-3 py-2 cursor-pointer capitalize text-sm font-semibold">
                      <Checkbox data-testid={`package-role-${role}`} checked={form.roles.includes(role)} onCheckedChange={() => toggleRole(role)} /> {role}
                    </label>
                  ))}
                </div>
              </div>
              {form.kind === "subscription" && (
                <div className="flex items-center justify-between border rounded-xl px-4 py-2">
                  <div>
                    <span className="font-semibold text-sm">Beginner package (one-time only)</span>
                    <p className="text-xs text-muted-foreground">Each seller can buy it once. Not renewable.</p>
                  </div>
                  <Switch data-testid="package-beginner" checked={form.beginner} onCheckedChange={(v) => setForm({ ...form, beginner: v })} />
                </div>
              )}
              <div className="flex items-center justify-between border rounded-xl px-4 py-2">
                <span className="font-semibold text-sm">Active (visible to sellers)</span>
                <Switch data-testid="package-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              </div>
              <Button data-testid="save-package-btn" onClick={save} className="w-full font-bold h-11">Save Package</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
